import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  achievements,
  athleteDevelopment,
  athletes,
  equipment,
  goals,
  matches,
  notifications,
  schedule,
  statistics,
  subscription,
  trainings,
} from '../../lib/api';
import {
  getGoalDataUpdatedAt,
  getMatchDataUpdatedAt,
  getScheduleDataUpdatedAt,
  getTrainingDataUpdatedAt,
  GOAL_DATA_UPDATED_EVENT,
  GOAL_DATA_UPDATED_KEY,
  MATCH_DATA_UPDATED_EVENT,
  MATCH_DATA_UPDATED_KEY,
  SCHEDULE_DATA_UPDATED_EVENT,
  SCHEDULE_DATA_UPDATED_KEY,
  TRAINING_DATA_UPDATED_EVENT,
  TRAINING_DATA_UPDATED_KEY,
} from '../../lib/dataFreshness';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  ArrowUpRight,
  Bell,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  Dumbbell,
  Gauge,
  Medal,
  PackageCheck,
  Plus,
  RefreshCcw,
  Sparkles,
  Target,
  Trophy,
  Zap,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

const TOOLTIP_STYLE = {
  backgroundColor: '#18181b',
  border: '1px solid rgba(183, 255, 42, 0.24)',
  borderRadius: '0.5rem',
  color: '#f9fafb',
};

const HERO_BG_URL =
  'https://images.pexels.com/photos/5739197/pexels-photo-5739197.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
const HERO_BG_STYLE = { backgroundImage: `url(${HERO_BG_URL})` };

const localeFor = (lang) =>
  lang === 'ru' ? 'ru-RU' : lang === 'fr' ? 'fr-FR' : 'en-GB';

const formatDate = (date, lang) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(localeFor(lang), {
    day: '2-digit',
    month: 'short',
  });
};

const EmptyState = ({ icon, title, body, action }) => (
  <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-5 text-center">
    <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-lg bg-white text-zinc-500 shadow-sm">
      {icon}
    </div>
    <h3 className="mt-3 text-sm font-bold text-zinc-900">{title}</h3>
    <p className="mt-1 text-sm leading-6 text-zinc-500">{body}</p>
    {action ? <div className="mt-4">{action}</div> : null}
  </div>
);

const SectionHeader = ({ kicker, title, action }) => (
  <div className="mb-4 flex items-end justify-between gap-4">
    <div>
      {kicker ? (
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
          {kicker}
        </p>
      ) : null}
      <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">
        {title}
      </h2>
    </div>
    {action}
  </div>
);

const QuickActionButton = ({ to, icon, label, tone = 'dark' }) => {
  const toneClass =
    tone === 'lime'
      ? 'border-lime-300 bg-lime-300 text-zinc-950 hover:bg-lime-200'
      : 'border-zinc-800 bg-zinc-950 text-white hover:bg-zinc-800';
  return (
    <Link
      to={to}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border px-4 py-2 text-sm font-black transition ${toneClass}`}
    >
      {icon}
      {label}
    </Link>
  );
};

const StatCard = ({ icon, label, value, detail, tone = 'lime' }) => {
  const toneClass =
    tone === 'cyan'
      ? 'bg-cyan-50 text-cyan-700 ring-cyan-100'
      : tone === 'zinc'
      ? 'bg-zinc-100 text-zinc-700 ring-zinc-200'
      : 'bg-lime-50 text-lime-700 ring-lime-100';
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-zinc-500">{label}</p>
          <p className="mt-2 text-3xl font-black tracking-normal text-zinc-950">
            {value}
          </p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ring-1 ${toneClass}`}>
          {icon}
        </div>
      </div>
      {detail ? <p className="mt-4 text-sm text-zinc-500">{detail}</p> : null}
    </div>
  );
};

const TrainingCard = ({ training, lang, t }) => (
  <article className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-xs font-black uppercase tracking-[0.12em] text-lime-700">
          {formatDate(training.date, lang) || t('common.date')}
        </p>
        <h3 className="mt-1 text-base font-black text-zinc-950">
          {training.type || t('common.type')}
        </h3>
      </div>
      <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-bold text-zinc-700">
        {training.duration || 0} {t('common.min')}
      </span>
    </div>
    <p className="mt-3 line-clamp-2 text-sm leading-6 text-zinc-500">
      {training.notes || t('dashboard.trainingNoNotes', 'No notes added yet.')}
    </p>
  </article>
);

const ActivityItem = ({ icon, title, body, meta }) => (
  <div className="flex gap-3 rounded-lg border border-zinc-200 bg-white p-4">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="font-bold text-zinc-950">{title}</p>
      <p className="mt-1 text-sm leading-5 text-zinc-500">{body}</p>
      {meta ? <p className="mt-2 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">{meta}</p> : null}
    </div>
  </div>
);

const normalizeDevelopmentRecommendations = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const developmentRecommendationStatusLabel = (status, t) => {
  const normalized = String(status || 'active').trim().toLowerCase();
  return t(`profile.development.recommendations.status.${normalized}`, status || t('profile.development.recommendations.active'));
};

const developmentRecommendationMetaLabel = (value, t) => {
  if (!value) return null;
  return t(`profile.development.recommendations.${String(value).toLowerCase()}`, value);
};

const DevelopmentRecommendationsDashboardSummary = ({ recommendations, loading, loadFailed, lang, t }) => {
  const topRecommendations = recommendations.slice(0, 3);

  return (
    <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-950 text-lime-300">
          <Sparkles size={18} strokeWidth={1.8} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
            {t('profile.development.recommendations.paid')}
          </p>
          <h3 className="mt-1 font-black text-zinc-950">
            {t('profile.development.recommendations.title')}
          </h3>
        </div>
      </div>

      {loading ? (
        <div className="mt-4 space-y-2">
          <div className="h-14 animate-pulse rounded-lg bg-white" />
          <div className="h-14 animate-pulse rounded-lg bg-white" />
        </div>
      ) : loadFailed ? (
        <p className="mt-4 rounded-lg bg-white px-3 py-2 text-sm font-bold text-zinc-500 ring-1 ring-zinc-200">
          {t('profile.development.recommendations.unavailable')}
        </p>
      ) : topRecommendations.length ? (
        <div className="mt-4 space-y-3">
          {topRecommendations.map((recommendation) => (
            <article key={recommendation.id || `${recommendation.title}-${recommendation.created_at}`} className="rounded-lg bg-white p-3 ring-1 ring-zinc-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-black uppercase tracking-[0.12em] text-lime-700">
                    {developmentRecommendationMetaLabel(recommendation.category, t)}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm font-black text-zinc-950">{recommendation.title}</p>
                </div>
                <span className="shrink-0 rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-black text-zinc-600">
                  {developmentRecommendationStatusLabel(recommendation.status, t)}
                </span>
              </div>
              <p className="mt-2 line-clamp-2 text-sm leading-5 text-zinc-500">{recommendation.rationale}</p>
              <p className="mt-2 text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">
                {formatDate(recommendation.created_at, lang) || t('common.date')}
              </p>
            </article>
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-dashed border-zinc-200 bg-white p-3">
          <p className="text-sm font-black text-zinc-950">
            {t('profile.development.recommendations.emptyTitle')}
          </p>
          <p className="mt-1 text-sm leading-5 text-zinc-500">
            {t('profile.development.recommendations.emptyBody')}
          </p>
        </div>
      )}

      <p className="mt-4 rounded-lg bg-lime-50 px-3 py-2 text-xs font-bold leading-5 text-lime-800 ring-1 ring-lime-100">
        {t('profile.development.recommendations.safetyNote')}
      </p>
    </div>
  );
};

const DataSignalRow = ({ icon, label, value, detail, to }) => {
  const content = (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-zinc-50 p-3">
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-zinc-700 shadow-sm">
          {icon}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-black text-zinc-950">{label}</p>
          {detail ? <p className="mt-1 truncate text-xs font-semibold text-zinc-500">{detail}</p> : null}
        </div>
      </div>
      <span className="shrink-0 text-lg font-black text-zinc-950">{value}</span>
    </div>
  );

  return to ? (
    <Link to={to} className="block transition hover:opacity-90">
      {content}
    </Link>
  ) : content;
};

const DevelopmentProfileDashboardCard = ({
  profile,
  loading,
  loadFailed,
  recommendations,
  recommendationsLoading,
  recommendationsFailed,
  lang,
  t,
}) => {
  const focusAreas = developmentSignalList(profile?.next_focus_areas);
  const strengths = developmentSignalList(profile?.strengths);
  const dataQualityFlags = developmentSignalList(profile?.data_quality_flags, 4);
  const updatedAt = formatDevelopmentDate(profile?.updated_at || profile?.generated_at, lang);
  const hasProfileData =
    Boolean(profile) &&
    (focusAreas.length > 0 ||
      strengths.length > 0 ||
      dataQualityFlags.length > 0 ||
      Object.keys(profile?.ai_confidence || {}).length > 0 ||
      updatedAt);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
      <SectionHeader
        kicker={t('profile.development.usedByPlanner', 'Used by AI Planner')}
        title={t('profile.development.shortTitle', 'Development profile')}
      />

      {loading ? (
        <div className="grid gap-3">
          <div className="h-16 animate-pulse rounded-lg bg-zinc-100" />
          <div className="h-16 animate-pulse rounded-lg bg-zinc-100" />
        </div>
      ) : loadFailed ? (
        <div className="rounded-lg bg-rose-50 p-3 text-sm font-bold text-rose-700 ring-1 ring-rose-100">
          {t('profile.development.loadError', 'Failed to load development profile')}
        </div>
      ) : hasProfileData ? (
        <div className="grid gap-4">
          <div className="grid gap-2 text-sm">
            <div className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2">
              <span className="font-bold text-zinc-500">{t('profile.development.lastUpdated', 'Last updated')}</span>
              <span className="text-right font-black text-zinc-950">{updatedAt || t('common.notSet', 'Not set')}</span>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2">
              <span className="font-bold text-zinc-500">{t('profile.development.confidence', 'Confidence')}</span>
              <span className="text-right font-black text-zinc-950">{confidenceSummary(profile, t)}</span>
            </div>
          </div>

          {focusAreas.length ? (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
                {t('profile.development.focusAreas', 'Focus areas')}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {focusAreas.map((item) => (
                  <span key={item} className="rounded-full bg-lime-50 px-3 py-1 text-xs font-bold text-lime-700 ring-1 ring-lime-100">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {strengths.length ? (
            <div>
              <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
                {t('profile.development.strengths', 'Strengths')}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {strengths.map((item) => (
                  <span key={item} className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-bold text-zinc-700">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-zinc-500">
              {t('profile.development.dataQuality', 'Data quality')}
            </p>
            <p className="mt-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold text-zinc-600">
              {dataQualityFlags.length ? dataQualityFlags.join(' · ') : t('common.notSet', 'Not set')}
            </p>
          </div>
        </div>
      ) : (
        <EmptyState
          icon={<Sparkles size={20} strokeWidth={1.8} />}
          title={t('profile.development.profileNotReady', 'Profile not ready yet')}
          body={t('profile.development.improveData', 'Add more training, match, wearable, recovery, and nutrition data to improve this profile.')}
        />
      )}

      {!loading && !loadFailed ? (
        <p className="mt-4 rounded-lg bg-lime-50 px-3 py-2 text-xs font-bold leading-5 text-lime-800 ring-1 ring-lime-100">
          {t('profile.development.safetyNote', 'This profile is for training guidance only and is not medical advice.')}
        </p>
      ) : null}

      <DevelopmentRecommendationsDashboardSummary
        recommendations={recommendations}
        loading={recommendationsLoading}
        loadFailed={recommendationsFailed}
        lang={lang}
        t={t}
      />
    </div>
  );
};

const normalizeListPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.results)) return data.results;
  return [];
};

const sortByDateDesc = (rows) =>
  [...rows].sort((a, b) => new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0));

const developmentSignalLabel = (item) => {
  if (!item) return null;
  if (typeof item === 'string') return item;
  if (typeof item === 'number') return String(item);
  return item.label || item.code || item.title || item.category || null;
};

const developmentSignalList = (items, limit = 3) =>
  (Array.isArray(items) ? items : []).map(developmentSignalLabel).filter(Boolean).slice(0, limit);

const formatDevelopmentDate = (value, lang) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(localeFor(lang), {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const confidenceSummary = (profile, t) => {
  const values = Object.values(profile?.ai_confidence || {}).filter(Boolean);
  if (!values.length) return t('common.notSet', 'Not set');
  const counts = values.reduce((acc, value) => {
    const key = String(value);
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([label, count]) => `${label}: ${count}`)
    .join(' · ');
};

const startOfCurrentWeek = () => {
  const now = new Date();
  const start = new Date(now);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  start.setHours(0, 0, 0, 0);
  return start;
};

const buildWeeklyTrainingSummary = (trainingRows) => {
  const weekStart = startOfCurrentWeek();
  const weekRows = trainingRows.filter((training) => {
    const parsed = new Date(training.date || training.created_at);
    return !Number.isNaN(parsed.getTime()) && parsed >= weekStart;
  });
  return {
    count: weekRows.length,
    minutes: weekRows.reduce((sum, training) => sum + (Number(training.duration) || 0), 0),
  };
};

const equipmentNeedsAttention = (item) => {
  const condition = String(item?.condition || '').trim().toLowerCase();
  return Boolean(item?.need_to_buy) || ['poor', 'worn', 'replace soon', 'replace_soon'].includes(condition);
};

const normalizeUnreadCount = (data) => {
  if (typeof data?.unread_count === 'number') return data.unread_count;
  return normalizeListPayload(data).filter((item) => !item.read_at).length;
};

const subscriptionStatusKey = (status) => {
  const normalized = String(status || 'none').trim().toLowerCase();
  if (normalized === 'past_due') return 'pastDue';
  if (normalized === 'cancelled') return 'canceled';
  return normalized || 'none';
};

const buildMatchActivity = (match, lang, t) => {
  const opponent = match.opponent_name || t('dashboard.unknownOpponent', 'Opponent');
  const resultLabel =
    match.result === 'win'
      ? t('match.win', 'Win')
      : match.result === 'loss'
      ? t('match.loss', 'Loss')
      : t('common.notSet', 'Not set');
  const scoreLabel = match.score
    ? `${t('match.score', 'Score')}: ${match.score}`
    : t('dashboard.noScoreRecorded', 'Score not recorded');
  const surfaceLabel = match.surface
    ? t(`court.surfaces.${match.surface}`, match.surface)
    : t('common.notSet', 'Not set');

  return {
    title: t('match.vsOpponent', { name: opponent, defaultValue: `vs ${opponent}` }),
    body: `${resultLabel} | ${scoreLabel} | ${surfaceLabel}`,
    meta: formatDate(match.date, lang) || t('common.date'),
  };
};

const goalProgressPercent = (goal) => {
  const current = Number(goal.current_value) || 0;
  const target = Number(goal.target_value) || 0;
  return target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;
};

const scheduleEventDate = (event) => {
  const parsed = new Date(event.date);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isUpcomingScheduleEvent = (event) => {
  const parsed = scheduleEventDate(event);
  if (!parsed) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed >= today;
};

const buildScheduleActivity = (event, lang, t) => {
  const timeRange = [event.start_time, event.end_time].filter(Boolean).join(' - ');
  const typeLabel = event.type
    ? t(`schedule.eventType.${event.type}`, event.type)
    : t('schedule.eventType.custom', 'Custom');
  const location = event.location || t('common.notSet', 'Not set');

  return {
    title: event.title || t('schedule.eventTitle', 'Event title'),
    body: `${typeLabel} | ${timeRange || t('dashboard.timeNotSet', 'Time not set')} | ${location}`,
    meta: formatDate(event.date, lang) || t('common.date'),
  };
};

export const AthleteDashboard = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [profile, setProfile] = useState(null);
  const [recentTrainings, setRecentTrainings] = useState([]);
  const [trainingCount, setTrainingCount] = useState(0);
  const [weeklyTrainingSummary, setWeeklyTrainingSummary] = useState({ count: 0, minutes: 0 });
  const [recentMatches, setRecentMatches] = useState([]);
  const [matchStats, setMatchStats] = useState(null);
  const [goalsList, setGoalsList] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [achievementStats, setAchievementStats] = useState(null);
  const [equipmentList, setEquipmentList] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [developmentProfile, setDevelopmentProfile] = useState(null);
  const [developmentProfileLoading, setDevelopmentProfileLoading] = useState(true);
  const [developmentProfileFailed, setDevelopmentProfileFailed] = useState(false);
  const [developmentRecommendations, setDevelopmentRecommendations] = useState([]);
  const [developmentRecommendationsLoading, setDevelopmentRecommendationsLoading] = useState(true);
  const [developmentRecommendationsFailed, setDevelopmentRecommendationsFailed] = useState(false);
  const [optionalDataAvailable, setOptionalDataAvailable] = useState({
    achievements: false,
    equipment: false,
    notifications: false,
    subscription: false,
  });
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const lastSeenTrainingUpdateRef = useRef(getTrainingDataUpdatedAt());
  const lastSeenMatchUpdateRef = useRef(getMatchDataUpdatedAt());
  const lastSeenGoalUpdateRef = useRef(getGoalDataUpdatedAt());
  const lastSeenScheduleUpdateRef = useRef(getScheduleDataUpdatedAt());
  const fetchInFlightRef = useRef(false);

  const fetchData = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setLoading(true);
    setLoadFailed(false);
    try {
      const [profileRes, trainingsRes, statsRes] = await Promise.all([
        athletes.getProfile(),
        trainings.getAll(),
        statistics.getAll(),
      ]);
      const [
        matchesResult,
        matchStatsResult,
        goalsResult,
        scheduleResult,
        achievementStatsResult,
        equipmentResult,
        notificationsResult,
        subscriptionResult,
      ] = await Promise.allSettled([
        matches.getAll(),
        matches.getStats(),
        goals.getAll(),
        schedule.getAll(),
        achievements.getStats(),
        equipment.getAll(),
        notifications.getAll({ limit: 20, offset: 0 }),
        subscription.get(),
      ]);

      setProfile(profileRes.data);
      const trainingRows = normalizeListPayload(trainingsRes.data);
      const sortedTrainings = sortByDateDesc(trainingRows);
      setTrainingCount(trainingRows.length);
      setWeeklyTrainingSummary(buildWeeklyTrainingSummary(trainingRows));
      setRecentTrainings(sortedTrainings.slice(0, 5));
      setStats(normalizeListPayload(statsRes.data).slice(-10));
      if (matchesResult.status === 'fulfilled') {
        const matchRows = normalizeListPayload(matchesResult.value.data);
        const sortedMatches = sortByDateDesc(matchRows);
        setRecentMatches(sortedMatches.slice(0, 4));
      } else {
        setRecentMatches([]);
      }
      if (matchStatsResult.status === 'fulfilled') {
        setMatchStats(matchStatsResult.value.data);
      } else {
        setMatchStats(null);
      }
      if (goalsResult.status === 'fulfilled') {
        setGoalsList(normalizeListPayload(goalsResult.value.data));
      } else {
        setGoalsList([]);
      }
      if (scheduleResult.status === 'fulfilled') {
        const scheduleRows = normalizeListPayload(scheduleResult.value.data);
        const sortedEvents = scheduleRows
          .filter(isUpcomingScheduleEvent)
          .sort((a, b) => {
            const dateDiff = scheduleEventDate(a) - scheduleEventDate(b);
            if (dateDiff !== 0) return dateDiff;
            return String(a.start_time || '').localeCompare(String(b.start_time || ''));
          });
        setUpcomingEvents(sortedEvents.slice(0, 4));
      } else {
        setUpcomingEvents([]);
      }
      setAchievementStats(
        achievementStatsResult.status === 'fulfilled' ? achievementStatsResult.value.data : null
      );
      setEquipmentList(
        equipmentResult.status === 'fulfilled' ? normalizeListPayload(equipmentResult.value.data) : []
      );
      setUnreadNotificationsCount(
        notificationsResult.status === 'fulfilled'
          ? normalizeUnreadCount(notificationsResult.value.data)
          : 0
      );
      setSubscriptionInfo(
        subscriptionResult.status === 'fulfilled' ? subscriptionResult.value.data : null
      );
      setOptionalDataAvailable({
        achievements: achievementStatsResult.status === 'fulfilled',
        equipment: equipmentResult.status === 'fulfilled',
        notifications: notificationsResult.status === 'fulfilled',
        subscription: subscriptionResult.status === 'fulfilled',
      });
      lastSeenTrainingUpdateRef.current = getTrainingDataUpdatedAt();
      lastSeenMatchUpdateRef.current = getMatchDataUpdatedAt();
      lastSeenGoalUpdateRef.current = getGoalDataUpdatedAt();
      lastSeenScheduleUpdateRef.current = getScheduleDataUpdatedAt();
    } catch (error) {
      setLoadFailed(true);
      toast.error(t('dashboard.loadError'));
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  }, [t]);

  const fetchDevelopmentProfile = useCallback(async () => {
    setDevelopmentProfileLoading(true);
    setDevelopmentProfileFailed(false);
    try {
      const response = await athleteDevelopment.getProfile();
      setDevelopmentProfile(response.data || null);
    } catch (error) {
      setDevelopmentProfile(null);
      setDevelopmentProfileFailed(true);
    } finally {
      setDevelopmentProfileLoading(false);
    }
  }, []);

  const fetchDevelopmentRecommendations = useCallback(async () => {
    setDevelopmentRecommendationsLoading(true);
    setDevelopmentRecommendationsFailed(false);
    try {
      const response = await athleteDevelopment.getRecommendations();
      setDevelopmentRecommendations(normalizeDevelopmentRecommendations(response.data));
    } catch (error) {
      setDevelopmentRecommendations([]);
      setDevelopmentRecommendationsFailed(true);
    } finally {
      setDevelopmentRecommendationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchDevelopmentProfile();
  }, [fetchDevelopmentProfile]);

  useEffect(() => {
    fetchDevelopmentRecommendations();
  }, [fetchDevelopmentRecommendations]);

  useEffect(() => {
    const refetchIfAthleteDataChanged = () => {
      const latestTrainingUpdate = getTrainingDataUpdatedAt();
      const latestMatchUpdate = getMatchDataUpdatedAt();
      const latestGoalUpdate = getGoalDataUpdatedAt();
      const latestScheduleUpdate = getScheduleDataUpdatedAt();
      const trainingChanged =
        latestTrainingUpdate && latestTrainingUpdate !== lastSeenTrainingUpdateRef.current;
      const matchChanged =
        latestMatchUpdate && latestMatchUpdate !== lastSeenMatchUpdateRef.current;
      const goalChanged =
        latestGoalUpdate && latestGoalUpdate !== lastSeenGoalUpdateRef.current;
      const scheduleChanged =
        latestScheduleUpdate && latestScheduleUpdate !== lastSeenScheduleUpdateRef.current;
      if (trainingChanged || matchChanged || goalChanged || scheduleChanged) fetchData();
    };

    const handleTrainingUpdated = () => {
      refetchIfAthleteDataChanged();
    };

    const handleMatchUpdated = () => {
      refetchIfAthleteDataChanged();
    };

    const handleGoalUpdated = () => {
      refetchIfAthleteDataChanged();
    };

    const handleScheduleUpdated = () => {
      refetchIfAthleteDataChanged();
    };

    const handleStorage = (event) => {
      if (
        event.key === TRAINING_DATA_UPDATED_KEY ||
        event.key === MATCH_DATA_UPDATED_KEY ||
        event.key === GOAL_DATA_UPDATED_KEY ||
        event.key === SCHEDULE_DATA_UPDATED_KEY
      ) {
        refetchIfAthleteDataChanged();
      }
    };

    const handleFocus = () => {
      fetchData();
      fetchDevelopmentProfile();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
        fetchDevelopmentProfile();
      }
    };

    window.addEventListener(TRAINING_DATA_UPDATED_EVENT, handleTrainingUpdated);
    window.addEventListener(MATCH_DATA_UPDATED_EVENT, handleMatchUpdated);
    window.addEventListener(GOAL_DATA_UPDATED_EVENT, handleGoalUpdated);
    window.addEventListener(SCHEDULE_DATA_UPDATED_EVENT, handleScheduleUpdated);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener(TRAINING_DATA_UPDATED_EVENT, handleTrainingUpdated);
      window.removeEventListener(MATCH_DATA_UPDATED_EVENT, handleMatchUpdated);
      window.removeEventListener(GOAL_DATA_UPDATED_EVENT, handleGoalUpdated);
      window.removeEventListener(SCHEDULE_DATA_UPDATED_EVENT, handleScheduleUpdated);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData, fetchDevelopmentProfile]);

  const chartData = useMemo(
    () =>
      stats.map((stat) => ({
        date: new Date(stat.date).toLocaleDateString(localeFor(i18n.language), {
          day: '2-digit',
          month: '2-digit',
        }),
        speed: stat.serve_speed || 0,
        accuracy: stat.accuracy || 0,
      })),
    [stats, i18n.language]
  );

  const avgStats = useMemo(() => {
    const speed =
      stats.length > 0
        ? (
            stats.reduce((sum, s) => sum + (s.serve_speed || 0), 0) /
            stats.length
          ).toFixed(1)
        : 0;
    const accuracy =
      stats.length > 0
        ? (
            stats.reduce((sum, s) => sum + (s.accuracy || 0), 0) / stats.length
          ).toFixed(1)
        : 0;
    return { speed, accuracy, totalTrainings: trainingCount };
  }, [stats, trainingCount]);

  const matchSummary = useMemo(() => {
    const totalMatches = matchStats?.total_matches ?? recentMatches.length;
    const winPercentage =
      matchStats?.win_percentage ?? matchStats?.win_rate ?? matchStats?.winRate ?? 0;
    return {
      totalMatches,
      winPercentage: Number.isFinite(Number(winPercentage))
        ? Math.round(Number(winPercentage))
        : 0,
    };
  }, [matchStats, recentMatches.length]);

  const goalSummary = useMemo(() => {
    if (goalsList.length === 0) {
      return { average: 0, activeGoal: null, activeProgress: 0, activeCount: 0, completedCount: 0 };
    }

    const activeGoals = goalsList.filter((goal) => {
      const status = String(goal.status || 'active').toLowerCase();
      return status !== 'completed' && status !== 'abandoned';
    });
    const completedCount = goalsList.filter(
      (goal) => String(goal.status || '').toLowerCase() === 'completed'
    ).length;
    const activeGoal =
      activeGoals[0] ||
      goalsList[0];
    const average = Math.round(
      goalsList.reduce((sum, goal) => sum + goalProgressPercent(goal), 0) /
        goalsList.length
    );

    return {
      average,
      activeGoal,
      activeProgress: goalProgressPercent(activeGoal),
      activeCount: activeGoals.length,
      completedCount,
    };
  }, [goalsList]);

  const achievementSummary = useMemo(() => {
    const total = Number(achievementStats?.total_achievements) || 0;
    const unlocked = Number(achievementStats?.unlocked_count) || 0;
    const completion = Number(achievementStats?.completion_percentage) || 0;
    return { total, unlocked, completion: Math.round(completion) };
  }, [achievementStats]);

  const equipmentSummary = useMemo(() => {
    const attentionCount = equipmentList.filter(equipmentNeedsAttention).length;
    return {
      total: equipmentList.length,
      attentionCount,
    };
  }, [equipmentList]);

  const subscriptionSummary = useMemo(() => {
    if (!subscriptionInfo) return null;
    const plan = String(subscriptionInfo.plan_id || subscriptionInfo.plan_type || 'free').toLowerCase();
    const status = subscriptionStatusKey(subscriptionInfo.status);
    return {
      plan,
      status,
      planLabel: t(`subscription.plans.${plan}`, plan),
      statusLabel: t(`subscription.status.${status}`, status),
    };
  }, [subscriptionInfo, t]);

  const heroName = user?.name || profile?.name || t('dashboard.athlete', 'Athlete');
  const latestTraining = recentTrainings[0];
  const nextAction =
    recentTrainings.length === 0
      ? {
          label: t('dashboard.logFirstTraining', 'Log first training'),
          body: t('dashboard.logFirstTrainingBody', 'Start your trail with one completed session.'),
          to: '/trainings',
          icon: <Plus size={18} strokeWidth={1.8} />,
        }
      : stats.length === 0
      ? {
          label: t('dashboard.addProgressStats', 'Add progress stats'),
          body: t('dashboard.addProgressStatsBody', 'Record serve speed or accuracy to unlock trend insights.'),
          to: '/progress',
          icon: <Gauge size={18} strokeWidth={1.8} />,
        }
      : {
          label: t('dashboard.reviewProgress', 'Review progress'),
          body: t('dashboard.reviewProgressBody', 'Use your latest stats to plan the next training block.'),
          to: '/progress',
          icon: <ArrowUpRight size={18} strokeWidth={1.8} />,
        };

  if (loading) {
    return (
      <div className="min-h-[70vh] p-4 lg:p-8" data-testid="athlete-dashboard-loading">
        <div className="mx-auto grid max-w-7xl gap-4">
          <div className="h-56 animate-pulse rounded-lg bg-zinc-200" />
          <div className="grid gap-4 md:grid-cols-3">
            <div className="h-32 animate-pulse rounded-lg bg-zinc-200" />
            <div className="h-32 animate-pulse rounded-lg bg-zinc-200" />
            <div className="h-32 animate-pulse rounded-lg bg-zinc-200" />
          </div>
          <p className="text-center text-sm font-semibold text-zinc-500">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-zinc-100 px-4 py-5 text-zinc-950 lg:px-8 lg:py-8"
      data-testid="athlete-dashboard"
    >
      <div className="mx-auto grid max-w-7xl gap-5 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="grid gap-5">
          <section
            className="relative overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 p-5 text-white shadow-xl md:p-7"
            data-testid="dashboard-hero"
          >
            <div
              className="absolute inset-0 bg-cover bg-center opacity-24"
              style={HERO_BG_STYLE}
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 bg-[linear-gradient(90deg,rgba(183,255,42,0.18)_1px,transparent_1px),linear-gradient(0deg,rgba(255,255,255,0.09)_1px,transparent_1px)] bg-[size:72px_72px] opacity-45"
              aria-hidden="true"
            />
            <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1fr)_17rem]">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-lime-200">
                  <Sparkles size={14} strokeWidth={1.8} />
                  {t('common.athleteDiary', 'Athlete diary')}
                </div>
                <h1 className="mt-5 max-w-3xl text-4xl font-black tracking-normal text-white md:text-5xl">
                  {t('dashboard.welcome', { name: heroName })}
                </h1>
                <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-200 md:text-lg">
                  {t('dashboard.subtitle')}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <QuickActionButton
                    to={nextAction.to}
                    icon={nextAction.icon}
                    label={nextAction.label}
                    tone="lime"
                  />
                  <QuickActionButton
                    to="/schedule"
                    icon={<CalendarDays size={18} strokeWidth={1.8} />}
                    label={t('dashboard.planSession', 'Plan session')}
                  />
                </div>
              </div>
              <div className="rounded-lg border border-white/12 bg-white/8 p-5 backdrop-blur">
                <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
                  {t('dashboard.thisWeekTraining', 'This week')}
                </p>
                <div className="mt-5 flex items-end gap-2">
                  <span className="text-6xl font-black tracking-normal text-white">
                    {weeklyTrainingSummary.count}
                  </span>
                  <span className="mb-2 text-sm font-bold text-zinc-300">
                    {t('dashboard.sessions', 'sessions')}
                  </span>
                </div>
                <p className="mt-4 text-sm font-bold text-lime-100">
                  {weeklyTrainingSummary.minutes} {t('common.min')}
                </p>
                <p className="mt-4 text-sm leading-6 text-zinc-300">
                  {t('dashboard.weeklyTrainingBody', 'Real training load from sessions dated this week.')}
                </p>
              </div>
            </div>
          </section>

          {loadFailed ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 text-amber-950">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="font-black">{t('dashboard.loadError')}</h2>
                  <p className="mt-1 text-sm text-amber-800">
                    {t('dashboard.loadErrorBody', 'Some dashboard data could not be refreshed.')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={fetchData}
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-4 text-sm font-black text-amber-950"
                >
                  <RefreshCcw size={16} strokeWidth={1.8} />
                  {t('common.retry', 'Retry')}
                </button>
              </div>
            </div>
          ) : null}

          <section className="grid gap-4 md:grid-cols-3">
            <StatCard
              icon={<Dumbbell size={22} strokeWidth={1.7} />}
              label={t('dashboard.trainingsCount')}
              value={avgStats.totalTrainings}
              detail={t('dashboard.recentWindow', 'Recent sessions loaded')}
              tone="lime"
            />
            <StatCard
              icon={<Zap size={22} strokeWidth={1.7} />}
              label={t('dashboard.avgServeSpeed')}
              value={`${avgStats.speed} ${t('common.kmh')}`}
              detail={t('dashboard.statFromRecent', 'Based on recent statistics')}
              tone="cyan"
            />
            <StatCard
              icon={<Target size={22} strokeWidth={1.7} />}
              label={t('dashboard.accuracy')}
              value={`${avgStats.accuracy}%`}
              detail={t('dashboard.statFromRecent', 'Based on recent statistics')}
              tone="zinc"
            />
          </section>

          <section className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)]">
            <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <SectionHeader
                kicker={t('dashboard.performance', 'Performance')}
                title={t('dashboard.progress')}
                action={
                  <Link to="/progress" className="text-sm font-black text-zinc-950 hover:text-lime-700">
                    {t('common.viewAll', 'View all')}
                  </Link>
                }
              />
              {chartData.length > 0 ? (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                      <XAxis dataKey="date" stroke="#71717a" tickLine={false} />
                      <YAxis stroke="#71717a" tickLine={false} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Line
                        type="monotone"
                        dataKey="speed"
                        stroke="#84cc16"
                        strokeWidth={3}
                        dot={false}
                        name={t('dashboard.serveSpeed')}
                      />
                      <Line
                        type="monotone"
                        dataKey="accuracy"
                        stroke="#06b6d4"
                        strokeWidth={3}
                        dot={false}
                        name={t('dashboard.accuracy')}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState
                  icon={<Gauge size={20} strokeWidth={1.8} />}
                  title={t('dashboard.noStatsTitle', 'No performance stats yet')}
                  body={t('dashboard.noStatsBody', 'Add statistics after sessions to see your trend line here.')}
                  action={
                    <QuickActionButton
                      to="/progress"
                      icon={<Plus size={16} strokeWidth={1.8} />}
                      label={t('dashboard.addStats', 'Add stats')}
                      tone="lime"
                    />
                  }
                />
              )}
            </div>

            <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <SectionHeader
                kicker={t('dashboard.nextBestAction', 'Next best action')}
                title={nextAction.label}
              />
              <ActivityItem
                icon={<CheckCircle2 size={18} strokeWidth={1.8} />}
                title={nextAction.label}
                body={nextAction.body}
                meta={latestTraining ? formatDate(latestTraining.date, i18n.language) : t('dashboard.readyWhenYouAre', 'Ready when you are')}
              />
              <div className="mt-4">
                <QuickActionButton
                  to={nextAction.to}
                  icon={nextAction.icon}
                  label={nextAction.label}
                  tone="lime"
                />
              </div>
            </div>
          </section>

          <section className="grid gap-5 lg:grid-cols-2">
            <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
              <SectionHeader
                kicker={t('dashboard.training', 'Training')}
                title={t('dashboard.recentTrainings')}
                action={
                  <Link to="/trainings" className="text-sm font-black text-zinc-950 hover:text-lime-700">
                    {t('common.viewAll', 'View all')}
                  </Link>
                }
              />
              {recentTrainings.length === 0 ? (
                <EmptyState
                  icon={<Dumbbell size={20} strokeWidth={1.8} />}
                  title={t('dashboard.noTrainings')}
                  body={t('dashboard.noTrainingsBody', 'Log your first session to build a useful training trail.')}
                  action={
                    <QuickActionButton
                      to="/trainings"
                      icon={<Plus size={16} strokeWidth={1.8} />}
                      label={t('dashboard.logTraining', 'Log training')}
                      tone="lime"
                    />
                  }
                />
              ) : (
                <div className="grid gap-3">
                  {recentTrainings.map((training) => (
                    <TrainingCard
                      key={training.id}
                      training={training}
                      lang={i18n.language}
                      t={t}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="grid gap-5">
              <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <SectionHeader
                  kicker={t('dashboard.matches', 'Matches')}
                  title={t('dashboard.recentMatches', 'Recent matches')}
                />
                {recentMatches.length === 0 ? (
                  <EmptyState
                    icon={<Trophy size={20} strokeWidth={1.8} />}
                    title={t('dashboard.noMatchesTitle', 'No match data loaded')}
                    body={t('dashboard.noMatchesBody', 'Add match results when you are ready to compare competitive form.')}
                    action={
                      <QuickActionButton
                        to="/matches"
                        icon={<Plus size={16} strokeWidth={1.8} />}
                        label={t('dashboard.addMatch', 'Add match')}
                        tone="lime"
                      />
                    }
                  />
                ) : (
                  <div className="grid gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                        <p className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">
                          {t('match.totalMatches', 'Total matches')}
                        </p>
                        <p className="mt-1 text-2xl font-black text-zinc-950">
                          {matchSummary.totalMatches}
                        </p>
                      </div>
                      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3">
                        <p className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">
                          {t('match.winPercentage', 'Win %')}
                        </p>
                        <p className="mt-1 text-2xl font-black text-zinc-950">
                          {matchSummary.winPercentage}%
                        </p>
                      </div>
                    </div>
                    {recentMatches.map((match) => {
                      const item = buildMatchActivity(match, i18n.language, t);
                      return (
                        <ActivityItem
                          key={match.id}
                          icon={<Trophy size={18} strokeWidth={1.8} />}
                          title={item.title}
                          body={item.body}
                          meta={item.meta}
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
                <SectionHeader
                  kicker={t('dashboard.goals', 'Goals')}
                  title={t('dashboard.goalProgress', 'Goal progress')}
                />
                {goalsList.length === 0 ? (
                  <EmptyState
                    icon={<Target size={20} strokeWidth={1.8} />}
                    title={t('progress.noGoals', 'No goals yet')}
                    body={t('progress.noGoalsBody', 'Create a goal to track progress toward a clear tennis outcome.')}
                    action={
                      <QuickActionButton
                        to="/progress"
                        icon={<Plus size={16} strokeWidth={1.8} />}
                        label={t('progress.addGoal', 'Add goal')}
                        tone="lime"
                      />
                    }
                  />
                ) : (
                  <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-black text-zinc-950">
                          {goalSummary.activeGoal?.title || t('dashboard.goalProgress', 'Goal progress')}
                        </p>
                        <p className="mt-1 text-sm text-zinc-500">
                          {goalSummary.activeGoal?.description ||
                            t('dashboard.goalProgressBody', 'Average progress across your current goals.')}
                        </p>
                      </div>
                      <span className="text-2xl font-black text-zinc-950">
                        {goalSummary.average}%
                      </span>
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-zinc-200">
                      <div
                        className="h-full rounded-full bg-lime-400"
                        style={{ width: `${goalSummary.average}%` }}
                      />
                    </div>
                    <p className="mt-3 text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">
                      {t('dashboard.activeGoalProgress', 'Active goal')}: {goalSummary.activeProgress}%
                    </p>
                    <div className="mt-3 grid grid-cols-2 gap-3 text-xs font-bold uppercase tracking-[0.12em] text-zinc-500">
                      <span>
                        {t('dashboard.activeGoals', 'Active goals')}: {goalSummary.activeCount}
                      </span>
                      <span>
                        {t('dashboard.completedGoals', 'Completed')}: {goalSummary.completedCount}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        <aside className="grid gap-5 xl:sticky xl:top-8 xl:self-start">
          <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <SectionHeader
              kicker={t('dashboard.schedule', 'Schedule')}
              title={t('dashboard.upcoming', 'Upcoming')}
            />
            {upcomingEvents.length === 0 ? (
              <EmptyState
                icon={<CalendarDays size={20} strokeWidth={1.8} />}
                title={t('dashboard.noUpcomingTitle', 'No upcoming items')}
                body={t('dashboard.noUpcomingBody', 'Plan a training block or review your calendar.')}
                action={
                  <QuickActionButton
                    to="/schedule"
                    icon={<Plus size={16} strokeWidth={1.8} />}
                    label={t('dashboard.planSession', 'Plan session')}
                    tone="lime"
                  />
                }
              />
            ) : (
              <div className="grid gap-3">
                {upcomingEvents.map((event) => {
                  const item = buildScheduleActivity(event, i18n.language, t);
                  return (
                    <ActivityItem
                      key={event.id}
                      icon={<CalendarDays size={18} strokeWidth={1.8} />}
                      title={item.title}
                      body={item.body}
                      meta={item.meta}
                    />
                  );
                })}
                <QuickActionButton
                  to="/schedule"
                  icon={<Plus size={16} strokeWidth={1.8} />}
                  label={t('dashboard.planSession', 'Plan session')}
                  tone="lime"
                />
              </div>
            )}
          </div>

          <DevelopmentProfileDashboardCard
            profile={developmentProfile}
            loading={developmentProfileLoading}
            loadFailed={developmentProfileFailed}
            recommendations={developmentRecommendations}
            recommendationsLoading={developmentRecommendationsLoading}
            recommendationsFailed={developmentRecommendationsFailed}
            lang={i18n.language}
            t={t}
          />

          <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <SectionHeader
              kicker={t('dashboard.realData', 'Real data')}
              title={t('dashboard.currentState', 'Current state')}
            />
            <div className="grid gap-3">
              <DataSignalRow
                to="/achievements"
                icon={<Medal size={18} strokeWidth={1.8} />}
                label={t('dashboard.achievementsUnlocked', 'Achievements unlocked')}
                value={
                  optionalDataAvailable.achievements
                    ? `${achievementSummary.unlocked}/${achievementSummary.total}`
                    : t('common.notSet', 'Not set')
                }
                detail={optionalDataAvailable.achievements ? `${achievementSummary.completion}%` : t('common.notSet', 'Not set')}
              />
              <DataSignalRow
                to="/equipment"
                icon={<PackageCheck size={18} strokeWidth={1.8} />}
                label={t('dashboard.equipmentAttention', 'Equipment attention')}
                value={
                  optionalDataAvailable.equipment
                    ? equipmentSummary.attentionCount
                    : t('common.notSet', 'Not set')
                }
                detail={
                  !optionalDataAvailable.equipment
                    ? t('common.notSet', 'Not set')
                    : equipmentSummary.attentionCount > 0
                    ? t('dashboard.itemsNeedAttention', {
                        count: equipmentSummary.attentionCount,
                        defaultValue: '{{count}} need attention',
                      })
                    : t('dashboard.noEquipmentAttention', 'No items need attention')
                }
              />
              <DataSignalRow
                to="/notifications"
                icon={<Bell size={18} strokeWidth={1.8} />}
                label={t('dashboard.unreadNotifications', 'Unread notifications')}
                value={
                  optionalDataAvailable.notifications
                    ? unreadNotificationsCount
                    : t('common.notSet', 'Not set')
                }
                detail={
                  !optionalDataAvailable.notifications
                    ? t('common.notSet', 'Not set')
                    : unreadNotificationsCount > 0
                    ? t('notifications.unreadCount', { count: unreadNotificationsCount })
                    : t('dashboard.notificationsClear', 'Inbox clear')
                }
              />
              <DataSignalRow
                to="/subscription"
                icon={<CreditCard size={18} strokeWidth={1.8} />}
                label={t('dashboard.subscriptionStatus', 'Subscription status')}
                value={
                  optionalDataAvailable.subscription && subscriptionSummary
                    ? subscriptionSummary.planLabel
                    : t('common.notSet', 'Not set')
                }
                detail={
                  optionalDataAvailable.subscription && subscriptionSummary
                    ? subscriptionSummary.statusLabel
                    : t('common.notSet', 'Not set')
                }
              />
            </div>
          </div>

          <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-5 text-white shadow-sm">
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
              {t('dashboard.quickActions', 'Quick actions')}
            </p>
            <div className="mt-4 grid gap-3">
              <QuickActionButton
                to="/trainings"
                icon={<Dumbbell size={16} strokeWidth={1.8} />}
                label={t('dashboard.logTraining', 'Log training')}
                tone="lime"
              />
              <QuickActionButton
                to="/matches"
                icon={<Trophy size={16} strokeWidth={1.8} />}
                label={t('dashboard.addMatch', 'Add match')}
              />
              <QuickActionButton
                to="/progress"
                icon={<Activity size={16} strokeWidth={1.8} />}
                label={t('dashboard.openProgress', 'Open progress')}
              />
            </div>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-5 shadow-sm">
            <SectionHeader
              kicker={t('dashboard.profile', 'Profile')}
              title={t('dashboard.athleteSnapshot', 'Athlete snapshot')}
            />
            <div className="grid gap-3 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">{t('profile.level', 'Level')}</span>
                <span className="font-bold text-zinc-950">{profile?.level || t('common.notSet', 'Not set')}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">{t('profile.rank', 'Rank')}</span>
                <span className="font-bold text-zinc-950">{profile?.rank || t('common.notSet', 'Not set')}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-zinc-500">{t('profile.hand', 'Hand')}</span>
                <span className="font-bold text-zinc-950">{profile?.dominant_hand || t('common.notSet', 'Not set')}</span>
              </div>
            </div>
            <Link
              to="/profile"
              className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-4 text-sm font-black text-zinc-950 hover:bg-zinc-100"
            >
              <ClipboardList size={16} strokeWidth={1.8} />
              {t('dashboard.updateProfile', 'Update profile')}
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
};
